import React, { useState, useEffect, useCallback } from "react";
import { connect, ConnectedProps } from "react-redux";
import { Formik, FormikProps } from "formik";
import * as Yup from "yup";
import { useHistory } from "react-router-dom";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Box from "@mui/material/Box";
import Switch from "@mui/material/Switch";
import PageHeader from "&styled/page-header";
import { RootState } from "&store/store";
import { merchantsActions } from "&features/merchants/merchants.slice";
import { settlementActions } from "&features/settlement/settlement.slice";
import SelectComponent from "&styled/form/select";
import { SubmitButton } from "&styled/button/button.component";
import { TextInput } from "&styled/textField/textField.component";
import {currencies} from '&config/currencies';

type ReduxProps = ConnectedProps<typeof connector>;

const MerchantFormComponent = (props: ReduxProps) => {
  const history = useHistory();
  const { getMerchant, addMerchant, editMerchant, merchantId } = props;

  const [data, setData] = useState({
    _id: "",
    country: "",
    name: "",
    currency: "",
    notificationEmail: "",
    supportEmail: "",
    billingEmail: "",
    commercialEmail: "",
    showBalance: false,
    orderPrefixEnable: false,
    orderPrefix: "",
    logo: "",
  });

  const validation = Yup.object().shape({
    country: Yup.string().required("Please select a department"),
    name: Yup.string()
      .min(2, "Too short account name.")
      .max(50, "Too long account name")
      .required("Please provide valid account name"),
    currency: Yup.string()
      .min(2, "Provided currency is invalid")
      .max(4, "Provided currency is invalid")
      .required("Please provide currency"),
    notificationEmail: Yup.string().optional(),
    supportEmail: Yup.string().optional(),
    billingEmail: Yup.string().optional(),
    commercialEmail: Yup.string().optional(),
    orderPrefixEnable: Yup.boolean(),
    orderPrefix: Yup.string().when("orderPrefixEnable", {
      is: true,
      then: Yup.string()
        .required("Please provide order prefix")
        .max(6, "Order prefix must be at most 6 characters")
        .matches(/^[A-Z-]+$/, "Order prefix must contain only capital letters and -"),
      otherwise: Yup.string().optional(),
    }),
  });

  const fetchMerchant = useCallback(
    async (id) => {
      try {
        const { payload } = await getMerchant(id);
        const merchant = { ...payload };
        if(!payload.showBalance){
          merchant.showBalance = false
        }
        delete merchant.createdDate;
        delete merchant.status;
        delete merchant.history;
        delete merchant.sid;
        delete merchant.publicKey;
        delete merchant.privateKey;
        delete merchant.token;
        delete merchant.__v;
        setData(merchant);
      } catch (ex) {
        console.log(ex);
        alert("Something went wrong");
      }
    },
    [getMerchant]
  );

  useEffect(() => {
    const id = merchantId;
    if (id === "new") return;
    fetchMerchant(id);
  }, [merchantId, fetchMerchant]);

  // Fetch revenue info

  const handleSubmit = async (vals) => {

    if (data._id) {
      await editMerchant(vals);

      history.push("/merchants");
    } else {
      const { payload } = await addMerchant(vals);
      if (payload && payload.status === "Active") {
        history.push("/merchants");
      }
    }
  };
  const countries = [
    { label: "Pakistan", value: "Pakistan" },
    { label: "China", value: "China" },
    { label: "Egypt", value: "Egypt" },
    { label: "Singapore", value: "Singapore" },
    { label: "Malaysia", value: "Malaysia" },
    { label: "United Kingdom", value: "United Kingdom" },
    { label: "United States", value: "United States" },
  ];
  return (
    <>
      <PageHeader title="Merchant Form" />
      <Box
        sx={{
          marginY: "1rem",
          borderTop: "4px solid #6631F7",
          borderTopLeftRadius: "4px",
          borderTopRightRadius: "4px",
        }}
      >
        <Card>
          <CardContent>
            <Formik
              enableReinitialize={true}
              initialValues={data}
              validateOnChange={true}
              validateOnBlur={true}
              onSubmit={(values: typeof data) => {
                handleSubmit(values);
              }}
              validationSchema={validation}
            >
              {(formik: FormikProps<typeof data>) => (
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <Box
                        component="label"
                        sx={{ cursor: "pointer", position: "relative", display: "inline-block" }}
                      >
                        <Box
                          sx={{
                            width: 90,
                            height: 90,
                            borderRadius: "50%",
                            border: "2px dashed #6631F7",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            overflow: "hidden",
                            backgroundColor: "#f5f0ff",
                            "&:hover .overlay": { opacity: 1 },
                          }}
                        >
                          {formik.values.logo ? (
                            <Box
                              component="img"
                              src={formik.values.logo}
                              alt="Merchant Logo"
                              sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                            />
                          ) : (
                            <Box sx={{ textAlign: "center", color: "#6631F7" }}>
                              <Box sx={{ fontSize: "1.5rem", lineHeight: 1 }}>+</Box>
                              <Box sx={{ fontSize: "0.65rem", mt: 0.5 }}>Logo</Box>
                            </Box>
                          )}
                          <Box
                            className="overlay"
                            sx={{
                              position: "absolute",
                              inset: 0,
                              bgcolor: "rgba(102,49,247,0.55)",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              borderRadius: "50%",
                              opacity: 0,
                              transition: "opacity 0.2s",
                              color: "#fff",
                              fontSize: "0.7rem",
                              fontWeight: 600,
                            }}
                          >
                            Change
                          </Box>
                        </Box>
                        <input
                          type="file"
                          accept="image/*"
                          hidden
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (!file) return;
                            const reader = new FileReader();
                            reader.onloadend = () => formik.setFieldValue("logo", reader.result as string);
                            reader.readAsDataURL(file);
                          }}
                        />
                      </Box>
                      <Box>
                        <Box sx={{ fontWeight: 600, fontSize: "0.9rem", color: "#2e2e2e" }}>Merchant Logo</Box>
                        <Box sx={{ fontSize: "0.75rem", color: "#727272", mt: 0.25 }}>PNG, JPG — recommended 256×256</Box>
                        {formik.values.logo && (
                          <Box
                            sx={{ fontSize: "0.75rem", color: "#e53935", cursor: "pointer", mt: 0.5 }}
                            onClick={() => formik.setFieldValue("logo", "")}
                          >
                            Remove
                          </Box>
                        )}
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item lg={4} sm={12}>
                    <SelectComponent
                      value={formik.values.country}
                      onSelect={formik.handleChange("country")}
                      menuItems={countries}
                      placeHolder="Select Country"
                    />
                  </Grid>

                  <Grid item lg={8} sm={12}></Grid>
                  <Grid item lg={4} sm={12}>
                    <TextInput
                      placeHolder={"Account Name"}
                      value={formik.values.name}
                      handleTextChange={formik.handleChange("name")}
                      hasError={!!formik.errors.name}
                      errorMessage={formik.errors.name as string}
                    />
                  </Grid>

                  <Grid item lg={8} sm={12}></Grid>
                  <Grid item lg={4} sm={12}>
                  <SelectComponent
                      value={formik.values.currency}
                      onSelect={formik.handleChange("currency")}
                      menuItems={currencies}
                      placeHolder="Select Currency"
                      hasError={!!formik.errors.currency}
                      errorMessage={formik.errors.currency as string}
                    />
                   
                  </Grid>

                  <Grid item lg={8} sm={12}></Grid>
                  <Grid item lg={4} sm={12}>
                    <TextInput
                      placeHolder={"Notification Email"}
                      required={false}
                      value={formik.values.notificationEmail}
                      handleTextChange={formik.handleChange(
                        "notificationEmail"
                      )}
                      hasError={!!formik.errors.notificationEmail}
                      errorMessage={formik.errors.notificationEmail as string}
                    />
                  </Grid>

                  <Grid item lg={8} sm={12}></Grid>
                  <Grid item lg={4} sm={12}>
                    <TextInput
                      placeHolder={"Support Email"}
                      required={false}
                      value={formik.values.supportEmail}
                      handleTextChange={formik.handleChange("supportEmail")}
                      hasError={!!formik.errors.supportEmail}
                      errorMessage={formik.errors.supportEmail as string}
                    />
                  </Grid>

                  <Grid item lg={8} sm={12}></Grid>
                  <Grid item lg={4} sm={12}>
                    <TextInput
                      placeHolder={"Billing Email"}
                      required={false}
                      value={formik.values.billingEmail}
                      handleTextChange={formik.handleChange("billingEmail")}
                      hasError={!!formik.errors.billingEmail}
                      errorMessage={formik.errors.billingEmail as string}
                    />
                  </Grid>
                  <Grid item lg={8} sm={12}></Grid>
                  <Grid item lg={4} sm={12}>
                    <TextInput
                      placeHolder={"Commercial Email"}
                      required={false}
                      value={formik.values.commercialEmail}
                      handleTextChange={formik.handleChange("commercialEmail")}
                      hasError={!!formik.errors.commercialEmail}
                      errorMessage={formik.errors.commercialEmail as string}
                    />
                  </Grid>

                  <Grid item lg={8} sm={12}></Grid>
                  <Grid item lg={4} sm={12}>
                        <Box sx={{display:"flex", gap:2, alignItems:"center"}}>
                          <Box>Show Balance</Box>
                          <Switch
                            checked={formik.values.showBalance}
                            onChange={formik.handleChange("showBalance")}
                            inputProps={{ "aria-label": "controlled" }}
                            color="success"
                          />
                        </Box>
                         
                  </Grid>

                  <Grid item lg={8} sm={12}></Grid>

                  <Grid item lg={4} sm={12}>
                    <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                      <Box>Enable Order Prefix</Box>
                      <Switch
                        checked={formik.values.orderPrefixEnable}
                        onChange={(e) => {
                          formik.setFieldValue("orderPrefixEnable", e.target.checked);
                          if (!e.target.checked) {
                            formik.setFieldValue("orderPrefix", "");
                          }
                        }}
                        inputProps={{ "aria-label": "controlled" }}
                        color="success"
                      />
                    </Box>
                  </Grid>

                  <Grid item lg={8} sm={12}></Grid>

                  {formik.values.orderPrefixEnable && (
                    <>
                      <Grid item lg={4} sm={12}>
                        <TextInput
                          placeHolder={"Order Prefix"}
                          value={formik.values.orderPrefix}
                          handleTextChange={(val: string) =>
                            formik.setFieldValue(
                              "orderPrefix",
                              val
                                .toUpperCase()
                                .replace(/[^A-Z-]/g, "")
                                .slice(0, 6)
                            )
                          }
                          hasError={!!formik.errors.orderPrefix}
                          errorMessage={formik.errors.orderPrefix as string}
                        />
                      </Grid>
                      <Grid item lg={8} sm={12}></Grid>
                    </>
                  )}

                  <Grid item lg={4} marginTop={"3rem"}>
                    <SubmitButton
                      title="Save"
                      handlePress={() => {
                        formik.handleSubmit();
                      }}
                    />
                  </Grid>
                </Grid>
              )}
            </Formik>
          </CardContent>
        </Card>
      </Box>
    </>
  );
};

/**
 * Maps state variables from redux store to props of currect component
 * @param state
 */
const mapStateToProps = (state: RootState, ownProps) => ({
  merchantId: ownProps.match.params.merchantId,
});

/**
 * Maps actions from slices to props
 */
const mapDispatchToProps = {
  getMerchant: merchantsActions.getMerchant,
  getSettlement: settlementActions.getSettlement,
  getMerchantRevenue: merchantsActions.getMerchantRevenue,
  addMerchant: merchantsActions.addMerchant,
  editMerchant: merchantsActions.editMerchant,
};

/**
 * Connects component to redux store
 */
const connector = connect(mapStateToProps, mapDispatchToProps);
const MerchantFormComponentRedux = connector(MerchantFormComponent);

export { MerchantFormComponentRedux as MerchantFormComponent };
